
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Save, Plus, Trash2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { legacyApi } from "../../../services/legacyApi";
import { api } from "../../../services/api";
import { toast } from "sonner";
import { RequiredLabel } from "../../../components/forms/RequiredLabel";
import { FieldErrorText } from "../../../components/forms/FieldErrorText";
import { showValidationSummaryToast } from "../../../components/forms/ValidationSummaryToast";
import { focusFirstInvalidField, validateRequiredFields } from "../../../components/forms/formValidationUtils";

interface InvoiceItem {
    item_id: number;
    estimate_part_id?: number;
    so_detail_id?: number;
    description: string;
    quantity: number;
    max_quantity?: number;
    unit_price: number;
    amount: number;
}

interface SoInvoiceContext {
    so_id: number;
    estimate_id: number;
    customer_id: number;
    customer_name?: string;
    vat_percent: number;
    discount_percent: number;
}

export const DirectInvoice: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const soIdParam = Number(searchParams.get("so_id") || 0);
    const isFromSaleOrder = Number.isFinite(soIdParam) && soIdParam > 0;

    const [customers, setCustomers] = useState<any[]>([]);
    const [materials, setMaterials] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [soContext, setSoContext] = useState<SoInvoiceContext | null>(null);

    const [formData, setFormData] = useState({
        customer_id: "",
        date: new Date().toISOString().split("T")[0],
    });

    const [items, setItems] = useState<InvoiceItem[]>([]);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        loadData();
    }, [soIdParam]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [custData, matData] = await Promise.all([
                legacyApi.customers.list(),
                api.inventory.list()
            ]);
            setCustomers(Array.isArray(custData) ? custData : []);
            setMaterials(Array.isArray(matData) ? matData : []);

            if (isFromSaleOrder) {
                const so = await legacyApi.sales.getOrder(soIdParam);
                const customerId = Number(so.customer_id || 0);
                if (!customerId) {
                    throw new Error("Direct sale order is missing a customer");
                }
                setSoContext({
                    so_id: Number(so.so_id),
                    estimate_id: Number(so.estimate_id || 0),
                    customer_id: customerId,
                    customer_name: so.customer_name || "",
                    vat_percent: Number(so.vat_percent || 0),
                    discount_percent: Number(so.discount_percent || 0),
                });
                setFormData((prev) => ({ ...prev, customer_id: String(customerId) }));

                const soItems: InvoiceItem[] = (so.details || []).map((line: any) => {
                    const quantity = Math.max(
                        0,
                        Number(
                            line.remaining_invoice_qty !== undefined
                                ? line.remaining_invoice_qty
                                : (line.quantity || 0)
                        )
                    );
                    const unitPrice = Number(line.unit_price || 0);
                    return {
                        item_id: Number(line.item_id || 0),
                        estimate_part_id: line.estimate_part_id !== undefined && line.estimate_part_id !== null
                            ? Number(line.estimate_part_id)
                            : undefined,
                        so_detail_id: line.so_detail_id !== undefined && line.so_detail_id !== null
                            ? Number(line.so_detail_id)
                            : undefined,
                        description: String(line.part_name || line.item_name || "Item"),
                        quantity,
                        max_quantity: quantity,
                        unit_price: unitPrice,
                        amount: quantity * unitPrice,
                    };
                });
                setItems(soItems);
            }
        } catch (error) {
            console.error("Failed to load data:", error);
            toast.error((error as any)?.message || "Failed to load invoice data");
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddItem = () => {
        if (isFromSaleOrder) return;
        setItems([
            ...items,
            {
                item_id: 0,
                description: "",
                quantity: 1,
                unit_price: 0,
                amount: 0,
            },
        ]);
    };

    const handleRemoveItem = (index: number) => {
        if (isFromSaleOrder) return;
        const newItems = [...items];
        newItems.splice(index, 1);
        setItems(newItems);
    };

    const handleItemChange = (index: number, field: keyof InvoiceItem, value: any) => {
        const newItems = [...items];
        const item = { ...newItems[index], [field]: value };

        // Auto-populate description if item_id changes in standalone direct-invoice mode.
        if (field === 'item_id') {
            const selectedMat = materials.find(m => m.id === Number(value));
            if (selectedMat) {
                item.description = selectedMat.name;
            }
        }

        if (field === "quantity" && item.max_quantity !== undefined) {
            const nextQty = Number(value || 0);
            if (!Number.isNaN(nextQty) && nextQty > Number(item.max_quantity || 0)) {
                item.quantity = Number(item.max_quantity || 0);
            }
        }

        item.amount = Number(item.quantity || 0) * Number(item.unit_price || 0);
        newItems[index] = item;
        setItems(newItems);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validation = validateRequiredFields([
            { key: 'customer_id', label: 'Customer', value: formData.customer_id, required: true },
        ]);
        const nextErrors = { ...validation.errorsByField } as Record<string, string>;
        if (items.length === 0) nextErrors.items = 'Please add at least one item';

        setIsSubmitting(true);
        try {
            if (isFromSaleOrder && soContext) {
                const invoiceItems = items
                    .filter((item) => Number(item.quantity || 0) > 0)
                    .map((item) => ({
                        estimate_part_id: Number(item.estimate_part_id),
                        quantity: Number(item.quantity),
                    }));

                if (invoiceItems.length === 0) nextErrors.items = "Please enter quantity greater than 0 for at least one item";
                if (invoiceItems.some((item) => Number.isNaN(item.estimate_part_id))) {
                    toast.error("One or more SO lines are missing part mapping");
                    return;
                }
                if (Object.keys(nextErrors).length > 0) {
                    setFieldErrors(nextErrors);
                    showValidationSummaryToast();
                    focusFirstInvalidField(Object.keys(nextErrors)[0]);
                    return;
                }
                setFieldErrors({});

                await legacyApi.sales.createInvoice({
                    estimate_id: Number(soContext.estimate_id || 0),
                    so_id: Number(soContext.so_id),
                    invoice_date: formData.date,
                    items: invoiceItems,
                });
                toast.success("Invoice Created Successfully");
            } else {
                for (const item of items) {
                    if (!item.item_id) {
                        nextErrors.items = "All items must have a selected material";
                        break;
                    }
                    if (item.quantity <= 0) {
                        nextErrors.items = "Quantity must be greater than 0";
                        break;
                    }
                }
                if (Object.keys(nextErrors).length > 0) {
                    setFieldErrors(nextErrors);
                    showValidationSummaryToast();
                    focusFirstInvalidField(Object.keys(nextErrors)[0]);
                    return;
                }
                setFieldErrors({});

                const payload = {
                    customer_id: Number(formData.customer_id),
                    date: formData.date,
                    items: items.map(item => ({
                        material_id: Number(item.item_id),
                        part_name: item.description || materials.find(m => m.id === Number(item.item_id))?.name || 'Item',
                        quantity: Number(item.quantity),
                        unit_price: Number(item.unit_price)
                    }))
                };

                await api.invoices.createDirect(payload);
                toast.success("Direct Invoice Created Successfully");
            }
            navigate("/legacy/invoices");
        } catch (error: any) {
            console.error("Failed to submit:", error);
            toast.error(error.message || "Failed to create invoice");
        } finally {
            setIsSubmitting(false);
        }
    };

    const totalAmount = items.reduce((sum, item) => sum + item.amount, 0);
    const discountPercent = Number(soContext?.discount_percent || 0);
    const vatPercent = Number(soContext?.vat_percent || 0);
    const discountAmount = totalAmount * (discountPercent / 100);
    const taxableAmount = totalAmount - discountAmount;
    const vatAmount = taxableAmount * (vatPercent / 100);
    const grandTotal = taxableAmount + vatAmount;

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">
                        {soContext ? `Invoice for SO #${soContext.so_id}` : "Direct Invoice"}
                    </h1>
                    <p className="text-muted-foreground">
                        {soContext
                            ? "Customer and SO items are pre-filled from the selected sale order"
                            : "Create a new invoice without an order"}
                    </p>
                </div>
            </div>

            <div className="bg-card rounded-lg border shadow-sm p-6">
                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Header Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <RequiredLabel required htmlFor="customer_id">Customer</RequiredLabel>
                            <select
                                id="customer_id"
                                name="customer_id"
                                aria-invalid={!!fieldErrors.customer_id}
                                aria-required="true"
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                value={formData.customer_id}
                                onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                                disabled={isLoading || !!soContext}
                            >
                                <option value="">Select Customer...</option>
                                {customers.map((c) => (
                                    <option key={c.customer_id} value={c.customer_id}>
                                        {c.customer_name}
                                    </option>
                                ))}
                            </select>
                            <FieldErrorText id="customer_id_error" message={fieldErrors.customer_id} />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Date</label>
                            <input
                                type="date"
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                value={formData.date}
                                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                            />
                        </div>
                    </div>
                    {soContext && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Discount (%)</label>
                                <input
                                    type="number"
                                    value={discountPercent}
                                    disabled
                                    className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm"
                                />
                                <p className="text-xs text-muted-foreground">Inherited from Sale Order.</p>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">VAT (%)</label>
                                <input
                                    type="number"
                                    value={vatPercent}
                                    disabled
                                    className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm"
                                />
                                <p className="text-xs text-muted-foreground">Inherited from Sale Order.</p>
                            </div>
                        </div>
                    )}

                    {/* Items Grid */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold">Items</h3>
                            {!soContext && (
                                <Button type="button" variant="outline" size="sm" onClick={handleAddItem}>
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Item
                                </Button>
                            )}
                        </div>
                        {fieldErrors.items && <FieldErrorText message={fieldErrors.items} />}

                        <div className="border rounded-md overflow-hidden">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-muted/50 text-muted-foreground">
                                    <tr>
                                        <th className="px-4 py-3 font-medium">{soContext ? "Part" : "Material"}</th>
                                        <th className="px-4 py-3 font-medium w-24">Qty</th>
                                        <th className="px-4 py-3 font-medium w-32">Price</th>
                                        <th className="px-4 py-3 font-medium w-32 text-right">Amount</th>
                                        <th className="px-4 py-3 font-medium w-12"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {items.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                                                No items added. Click "Add Item" to start.
                                            </td>
                                        </tr>
                                    ) : (
                                        items.map((item, index) => (
                                            <tr key={index} className="bg-card">
                                                <td className="p-2">
                                                    {soContext ? (
                                                        <div className="py-2 px-1">
                                                            <div className="font-medium">{item.description}</div>
                                                            <div className="text-xs text-muted-foreground">
                                                                {(item.estimate_part_id ?? item.so_detail_id) !== undefined
                                                                    ? `Part ${item.estimate_part_id ?? item.so_detail_id}`
                                                                    : ""}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <select
                                                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                            value={item.item_id}
                                                            onChange={(e) => handleItemChange(index, "item_id", e.target.value)}
                                                        >
                                                            <option value={0}>Select Material...</option>
                                                            {materials.map((m) => (
                                                                <option key={`mat-${m.id}`} value={m.id}>
                                                                    {m.name} ({m.code})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    )}
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        type="number"
                                                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                        value={item.quantity}
                                                        onChange={(e) => handleItemChange(index, "quantity", e.target.value)}
                                                        min={soContext ? "0" : "1"}
                                                        max={item.max_quantity !== undefined ? String(item.max_quantity) : undefined}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        type="number"
                                                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                        value={item.unit_price}
                                                        onChange={(e) => handleItemChange(index, "unit_price", e.target.value)}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                </td>
                                                <td className="p-2 text-right font-medium">
                                                    {item.amount.toFixed(2)}
                                                </td>
                                                <td className="p-2 text-center">
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                                        disabled={!!soContext}
                                                        onClick={() => handleRemoveItem(index)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                                <tfoot className="bg-muted/50 font-medium">
                                    <tr>
                                        <td colSpan={3} className="px-4 py-2 text-right">Subtotal:</td>
                                        <td className="px-4 py-2 text-right">{totalAmount.toFixed(2)}</td>
                                        <td></td>
                                    </tr>
                                    {soContext && discountPercent > 0 && (
                                        <tr>
                                            <td colSpan={3} className="px-4 py-2 text-right">Discount ({discountPercent.toFixed(2)}%):</td>
                                            <td className="px-4 py-2 text-right text-red-600">-{discountAmount.toFixed(2)}</td>
                                            <td></td>
                                        </tr>
                                    )}
                                    {soContext && vatPercent > 0 && (
                                        <tr>
                                            <td colSpan={3} className="px-4 py-2 text-right">VAT ({vatPercent.toFixed(2)}%):</td>
                                            <td className="px-4 py-2 text-right text-green-700">{vatAmount.toFixed(2)}</td>
                                            <td></td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td colSpan={3} className="px-4 py-3 text-right">{soContext ? 'Grand Total:' : 'Total Amount:'}</td>
                                        <td className="px-4 py-3 text-right">{(soContext ? grandTotal : totalAmount).toFixed(2)}</td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>

                    <div className="flex justify-end pt-4">
                        <Button type="submit" disabled={isSubmitting || isLoading}>
                            {isSubmitting ? (
                                <>Saving...</>
                            ) : (
                                <>
                                    <Save className="h-4 w-4 mr-2" /> Create Invoice
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};
